import { ExtendedDataset } from "./types";
import {
  ChartOptions as ChartOptionsType,
  ChartEvent,
  ActiveElement,
  Chart,
  TooltipItem,
} from "chart.js";

const getChartOptions = (): ChartOptionsType<"line"> => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        display: true,
        grid: {
          display: false,
          color: "rgba(200, 200, 200, 0.1)",
        },
        ticks: {
          color: "rgba(150, 150, 150, 0.5)",
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
        },
        title: {
          display: true,
          text: "Date",
          color: "rgba(150, 150, 150, 0.5)",
        },
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: "rgba(200, 200, 200, 0.1)",
          // Remove drawBorder as it doesn't exist in the type
        },
        ticks: {
          color: "rgba(150, 150, 150, 0.5)",
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          padding: 8,
        },
        title: {
          display: true,
          text: "Score",
          color: "rgba(150, 150, 150, 0.5)",
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          usePointStyle: true,
          padding: 15,
          boxWidth: 6,
          pointStyle: "circle",
          boxHeight: 6,
          color: "rgba(150, 150, 150, 0.9)",
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        titleFont: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        padding: 10,
        cornerRadius: 4,
        callbacks: {
          title: function (tooltipItems: TooltipItem<"line">[]) {
            const datasetIndex = tooltipItems[0].datasetIndex;
            const index = tooltipItems[0].dataIndex;
            // Use type assertion for custom properties
            const dataset = tooltipItems[0].chart.data.datasets[
              datasetIndex
            ] as unknown as ExtendedDataset;

            // Access custom metadata
            const metadata = dataset.pointMetadata?.[index];
            return metadata?.reportName || "Unknown";
          },
          label: function (context: TooltipItem<"line">) {
            // Use type assertion for custom properties
            const dataset = context.chart.data.datasets[
              context.datasetIndex
            ] as unknown as ExtendedDataset;
            const metadata = dataset.pointMetadata?.[context.dataIndex];

            let lines: string[] = [];
            if (metadata) {
              lines.push(`Batch: ${metadata.batchName || "Unknown"}`);
              lines.push(`Score: ${context.formattedValue}`);
              lines.push(`Dataset: ${metadata.datasetName || "Unknown"}`);

              if (metadata.version) {
                lines.push(`Prompt Version: ${metadata.version}`);
              }
              if (metadata.timestamp) {
                lines.push(`Date: ${metadata.timestamp}`);
              }
              if (metadata.hasComment && metadata.comment) {
                lines.push(`Comment: ${metadata.comment}`);
              }
            }

            return lines;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 3,
        hoverRadius: 5,
        backgroundColor: "white",
        borderWidth: 1,
        hitRadius: 10,
      },
    },
    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      const target = event.native?.target as HTMLCanvasElement;
      if (elements.length > 0) {
        target.style.cursor = "pointer";
      } else {
        target.style.cursor = "default";
      }
    },
    onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length > 0) {
        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        // Use type assertion to access our custom properties
        const dataset = chart.data.datasets[
          datasetIndex
        ] as unknown as ExtendedDataset;
        const metadata = dataset.pointMetadata?.[index];

        if (metadata?.navigate) metadata.navigate();
      }
    },
  };
};

export { getChartOptions };
